import { Badge } from 'src/design/atoms/Badge';
import { CloseButton } from 'src/design/atoms/CloseButton';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { ButtonGroup } from 'src/design/atoms/ButtonGroup';
import { Menu } from 'src/design/molecules/Menu';
import { TextInput } from 'src/design/atoms/TextInput';
import {
  IconFilter,
  IconSearch,
  IconSortAscending,
  IconSortDescending,
  IconX,
} from '@tabler/icons-react';
import React from 'react';
import { PostFilterDto } from 'src/api/FiretrolApiClient';
import useDebounce from 'src/hooks/useDebounce';

interface FiltersProps {
  onFiltersChange: (field: string, value: string | boolean) => void;
  filters: PostFilterDto;
  onSortChange: (sort: string) => void;
  onFilterChange: (filter: string) => void;
  onPostTypeChange: (type: string) => void;
  onQueryChange: (query: string) => void;
  postType: string;
  sort: string;
  filter: string;
  query: string;
}

export const Filters: React.FC<FiltersProps> = ({
  onSortChange,
  sort,
  onFilterChange,
  onFiltersChange,
  filters,
  filter,
  postType,
  onPostTypeChange,
  query,
  onQueryChange,
}) => {
  const [filterAnchorEl, setFilterAnchorEl] =
    React.useState<null | HTMLElement>(null);
  const [sortAnchorEl, setSortAnchorEl] = React.useState<null | HTMLElement>(
    null,
  );
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [q, setQ] = React.useState(query);
  const debouncedQuery = useDebounce(q, 300);

  React.useEffect(() => {
    onQueryChange(debouncedQuery);
  }, [debouncedQuery]);

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const closeSearch = () => {
    setSearchOpen(false);
    onQueryChange('');
    setQ('');
  };

  return (
    <Stack>
      <Group>
        <ButtonGroup>
          <BasicButton
            variant={postType === 'all' ? 'filled' : 'outline'}
            onClick={() => onPostTypeChange('all')}
            size="xs"
          >
            All Posts
          </BasicButton>
          <BasicButton
            variant={postType === 'createdByMe' ? 'filled' : 'outline'}
            onClick={() => onPostTypeChange('createdByMe')}
            size="xs"
          >
            My Posts
          </BasicButton>
        </ButtonGroup>

        <Group>
          <BasicActionIcon
            variant={searchOpen ? 'filled' : 'light'}
            onClick={() => (searchOpen ? closeSearch() : setSearchOpen(true))}
          >
            <IconSearch size={16} />
          </BasicActionIcon>

          <Menu
            position="bottom-start"
            opened={!!filterAnchorEl}
            onClose={handleFilterClose}
          >
            <Menu.Target>
              <BasicActionIcon variant="light" onClick={handleFilterClick}>
                <IconFilter size={16} />
              </BasicActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>Filter</Menu.Label>
              <Menu.Item
                onClick={() => {
                  onFiltersChange('visibility', 'private');
                  handleFilterClose();
                }}
              >
                Private Posts
              </Menu.Item>
              <Menu.Item
                onClick={() => {
                  onFiltersChange('visibility', 'public');
                  handleFilterClose();
                }}
              >
                Public Posts
              </Menu.Item>
              <Menu.Item
                onClick={() => {
                  onFiltersChange('resolved', 'resolved');
                  handleFilterClose();
                }}
              >
                Resolved
              </Menu.Item>
              <Menu.Item
                onClick={() => {
                  onFiltersChange('resolved', 'unresolved');
                  handleFilterClose();
                }}
              >
                Unresolved
              </Menu.Item>
              <Menu.Item
                onClick={() => {
                  onFiltersChange('ctc', !filters.ctc);
                  handleFilterClose();
                }}
              >
                CTC
              </Menu.Item>
              <Menu.Item
                onClick={() => {
                  onFiltersChange('critical', !filters.critical);
                  handleFilterClose();
                }}
              >
                Critical
              </Menu.Item>
              <Menu.Item
                onClick={() => {
                  onFiltersChange('constraint', !filters.constraint);
                  handleFilterClose();
                }}
              >
                Constraint
              </Menu.Item>
              <Menu.Item
                onClick={() => {
                  onFiltersChange('cost', !filters.cost);
                  handleFilterClose();
                }}
              >
                Cost and Schedule
              </Menu.Item>
              <Menu.Item
                onClick={() => {
                  onFiltersChange('assigned', !filters.assigned);
                  handleFilterClose();
                }}
              >
                Assigned
              </Menu.Item>
              <Menu.Item
                onClick={() => {
                  onFiltersChange('assignedToMe', !filters.assignedToMe);
                  handleFilterClose();
                }}
              >
                Assigned To Me
              </Menu.Item>
              <Menu.Item
                onClick={() => {
                  onFiltersChange('createdByMe', !filters.createdByMe);
                  handleFilterClose();
                }}
              >
                Created By Me
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>

          <Menu
            position="bottom-start"
            opened={!!sortAnchorEl}
            onClose={handleSortClose}
          >
            <Menu.Target>
              <BasicActionIcon variant="light" onClick={handleSortClick}>
                {sort === 'asc' ? (
                  <IconSortAscending size={16} />
                ) : (
                  <IconSortDescending size={16} />
                )}
              </BasicActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>Sort By</Menu.Label>
              <Menu.Item
                onClick={() => {
                  onSortChange('updated_at');
                  handleSortClose();
                }}
              >
                Last Active
              </Menu.Item>
              <Menu.Item
                onClick={() => {
                  onSortChange('created_at');
                  handleSortClose();
                }}
              >
                Created Date
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Group>

      {(filters.visibility !== 'all' ||
        filters.resolved !== 'all' ||
        filters.constraint ||
        filters.createdByMe ||
        filters.assignedToMe ||
        filters.cost ||
        filters.ctc ||
        filters.assigned ||
        filters.critical) && (
        <Group>
          {filters.ctc && (
            <Badge
              rightSection={
                <CloseButton
                  onClick={() => onFiltersChange('ctc', false)}
                  size="xs"
                />
              }
            >
              CTC
            </Badge>
          )}
          {filters.resolved !== 'all' && (
            <Badge
              rightSection={
                <CloseButton
                  onClick={() => onFiltersChange('resolved', 'all')}
                  size="xs"
                />
              }
            >
              {filters.resolved}
            </Badge>
          )}
          {filters.visibility !== 'all' && (
            <Badge
              rightSection={
                <CloseButton
                  onClick={() => onFiltersChange('visibility', 'all')}
                  size="xs"
                />
              }
            >
              {filters.visibility}
            </Badge>
          )}
          {filters.assignedToMe && (
            <Badge
              rightSection={
                <CloseButton
                  onClick={() => onFiltersChange('assignedToMe', false)}
                  size="xs"
                />
              }
            >
              Assigned To Me
            </Badge>
          )}
          {filters.createdByMe && (
            <Badge
              rightSection={
                <CloseButton
                  onClick={() => onFiltersChange('createdByMe', false)}
                  size="xs"
                />
              }
            >
              Created By Me
            </Badge>
          )}
          {filters.constraint && (
            <Badge
              rightSection={
                <CloseButton
                  onClick={() => onFiltersChange('constraint', false)}
                  size="xs"
                />
              }
            >
              Constraint
            </Badge>
          )}
          {filters.cost && (
            <Badge
              rightSection={
                <CloseButton
                  onClick={() => onFiltersChange('cost', false)}
                  size="xs"
                />
              }
            >
              Cost and Schedule
            </Badge>
          )}
          {filters.assigned && (
            <Badge
              rightSection={
                <CloseButton
                  onClick={() => onFiltersChange('assigned', false)}
                  size="xs"
                />
              }
            >
              Assigned
            </Badge>
          )}
          {filters.critical && (
            <Badge
              rightSection={
                <CloseButton
                  onClick={() => onFiltersChange('critical', false)}
                  size="xs"
                />
              }
            >
              Critical
            </Badge>
          )}
        </Group>
      )}

      {searchOpen && (
        <TextInput
          placeholder="Search posts..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          rightSection={
            <BasicActionIcon variant="filled" onClick={closeSearch}>
              <IconX size={16} />
            </BasicActionIcon>
          }
        />
      )}
    </Stack>
  );
};
